import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, ArrowRight, MapPin, Users, MessageCircle, Ship, Video, Radio } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { brandLabel } from '../lib/brandMap';
import ProductCard from '../components/ProductCard';
import { fetchProducts } from '../data/products';
import { trackPageView } from '../lib/analytics';
import { useCMS } from '../hooks/useCMS';
import { useWhatsAppNumber, waLink } from '../hooks/useWhatsAppNumber';
import type { Product } from '../types';
import { SITE_NAME, SITE_TAGLINE } from '../lib/brand';

function shuffleProducts<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function Home() {
  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroError, setHeroError] = useState(false);
  const { getValue, getHtml, loading: cmsLoading } = useCMS();
  // [UPSTREAM] CMS 空值＝不用背景圖（品牌底色）；空字串不可餵 <img src>（觸發資源錯誤事件）
  const heroImage = getValue('home.hero.image', '').trim();

  useEffect(() => {
    trackPageView('/');
    fetchProducts().then((data) => {
      const shuffled = shuffleProducts(data);
      setProducts(shuffled.slice(0, 3));
      setAllProducts(shuffled);
      setLoading(false);
    });
  }, []);

  // 市集輪播：每 4 秒隨機換一張卡，展示商品多樣性（分頁隱藏時暫停）
  useEffect(() => {
    if (allProducts.length <= 3) return undefined;
    let slot = 0;
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      setProducts((current) => {
        const visibleIds = new Set(current.map((p) => p.id));
        const pool = allProducts.filter((p) => !visibleIds.has(p.id));
        if (pool.length === 0) return current;
        const next = pool[Math.floor(Math.random() * pool.length)];
        const copy = [...current];
        copy[slot % copy.length] = next;
        slot += 1;
        return copy;
      });
    }, 4000);
    return () => window.clearInterval(timer);
  }, [allProducts]);

  const whatsappNumber = useWhatsAppNumber();
  const whatsappReady = whatsappNumber.length >= 8;
  const whatsappFriendLink = whatsappReady ? waLink(whatsappNumber, '你好，我想查詢') : '';
  const whatsappGiftLink = whatsappReady ? waLink(whatsappNumber, '你好，我想了解優惠') : '';

  const trustItems = [
    { icon: Video, titleKey: 'home.trust.1.title', descKey: 'home.trust.1.description' },
    { icon: Ship, titleKey: 'home.trust.2.title', descKey: 'home.trust.2.description' },
    { icon: MapPin, titleKey: 'home.trust.3.title', descKey: 'home.trust.3.description' },
  ];

  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500">載入中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-x1493-dark text-white overflow-hidden min-h-[600px] md:min-h-[700px] flex items-center">
        {heroImage && !heroError && (
          <img
            src={heroImage}
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="async"
            onError={() => setHeroError(true)}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: 'translateZ(0)' }}
          />
        )}
        {heroImage && !heroError && (
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b3527]/90 via-[#0b3527]/65 to-[#0b3527]/30" aria-hidden="true" />
        )}
        <div className="absolute inset-0 bg-transparent" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-x1493-primary rounded-full blur-[120px] transform translate-x-1/3 -translate-y-1/3" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-x1493-secondary rounded-full blur-[100px] transform -translate-x-1/3 translate-y-1/3" />
        </div>
        <div className="max-w-6xl mx-auto px-4 py-20 md:py-32 relative z-10 w-full h-full">
          <div className="max-w-2xl h-full flex flex-col md:justify-center">
            <div className="flex-1 md:flex-none flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm mb-6 w-fit">
                <Flame size={16} className="text-x1493-secondary" />
                <span dangerouslySetInnerHTML={getHtml('home.hero.badge', SITE_TAGLINE)} />
              </div>
              <h1
                className="text-4xl md:text-6xl font-bold leading-tight mb-6"
                dangerouslySetInnerHTML={getHtml('home.hero.title', '精選好物<br />等你發掘')}
              />
              <p
                className="text-xl text-white font-bold mb-8 leading-relaxed bg-black/50 backdrop-blur-sm px-4 py-3 rounded-xl md:bg-transparent md:backdrop-blur-none md:px-0 md:py-0 md:rounded-none"
                dangerouslySetInnerHTML={getHtml('home.hero.subtitle', '一站式網上選購，方便快捷。<br class="hidden md:block" />WhatsApp 落單，專人跟進。')}
              />
              </div>
            <div className="flex flex-col sm:flex-row gap-4 mt-auto md:mt-0">
              <Link to="/store" className="btn-primary text-center flex items-center justify-center gap-2">
                {getValue('home.hero.primaryButtonText', '出海買港貨')}
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/b2b"
                className="border-2 border-x1493-secondary text-x1493-secondary text-center font-bold py-3 px-6 rounded-2xl hover:bg-x1493-secondary hover:text-x1493-dark transition-all flex items-center justify-center gap-2"
              >
                <Ship size={20} />
                {getValue('home.hero.secondaryButtonText', '品牌入駐出海')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {trustItems.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <div className="w-14 h-14 bg-x1493-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-x1493-primary" size={28} />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{getValue(item.titleKey)}</h3>
                  <p className="text-stone-600 text-sm">{getValue(item.descKey)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 品牌牆預覽（平台多品牌證明——讀 products 商戶欄動態生成） */}
      <section className="pb-14 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">{getValue('home.brands.title', '入駐品牌')}</h2>
            <p className="text-sm text-stone-500">{getValue('home.brands.subtitle', '香港中小企品牌 · 陸續有來')}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {Array.from(new Set(allProducts.map((p) => p.brand).filter(Boolean))).slice(0, 8).map((b) => (
              <span key={b} className="bg-white border border-stone-200 rounded-full px-5 py-2.5 text-sm font-bold text-stone-700">
                {b === 'gs6j' ? '⭐ ' : ''}{brandLabel(b)}
              </span>
            ))}
            <Link to="/b2b" className="rounded-full px-5 py-2.5 text-sm font-bold border-2 border-dashed border-x1493-primary text-x1493-primary hover:bg-x1493-primary hover:text-white transition">
              ＋ 你的品牌
            </Link>
          </div>
        </div>
      </section>

      {/* Product Highlights */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">{getValue('home.hot.title', '直播嚴選 · 熱賣中')}</h2>
            <p className="text-stone-600">{getValue('home.hot.subtitle', '坤哥直播試貨人氣榜——醬料、海味、禮盒，真人試食眼見為實')}</p>
          </div>

          {loading ? (
            <div className="text-center py-12">載入中...</div>
          ) : (
            <>
              <style>{`
                @keyframes home-product-in { from { opacity: 0; transform: translateY(10px) scale(0.98); } to { opacity: 1; transform: none; } }
                .home-product-swap { animation: home-product-in 0.55s ease-out both; }
                @media (prefers-reduced-motion: reduce) { .home-product-swap { animation: none; } }
              `}</style>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {products.map((product) => (
                  <div key={product.id} className="home-product-swap">
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="text-center mt-10">
            <Link to="/store" className="btn-outline inline-flex items-center gap-2">
              查看全部貨架
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Story Teaser */}
      <section className="py-16 bg-x1493-dark text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">{getValue('home.story.title', '我們的故事')}</h2>
              <p
                className="text-white/80 mb-6 leading-relaxed"
                dangerouslySetInnerHTML={getHtml('home.story.content', '在這裡寫下品牌的起點故事：為何而做、為誰而做、堅持什麼。真實的故事最動人。（後台 CMS 可編輯）')}
              />
              <Link to="/story" className="btn-secondary inline-flex items-center gap-2">
                {getValue('home.story.buttonText', '閱讀完整故事')}
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="bg-white/5 rounded-3xl p-8 border border-white/10">
              <h3 className="text-xl font-bold mb-5 text-white">{getValue('home.moat.title', '平台三護城河')}</h3>
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-x1493-primary flex items-center justify-center shrink-0 text-white"><Ship size={22} /></div>
                  <div>
                    <p className="font-bold">{getValue('home.moat.1.title', '十年跨境專線')}</p>
                    <p className="text-sm text-white/70">{getValue('home.moat.1.desc', '包稅清關一條龍——食品敏感貨可走')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-x1493-accent flex items-center justify-center shrink-0 text-x1493-dark"><Video size={22} /></div>
                  <div>
                    <p className="font-bold">{getValue('home.moat.2.title', '直播試貨獲客')}</p>
                    <p className="text-sm text-white/70">{getValue('home.moat.2.desc', '真人試過先上架——直播間就是櫥窗')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full border-2 border-x1493-accent flex items-center justify-center shrink-0 text-x1493-accent"><Radio size={22} /></div>
                  <div>
                    <p className="font-bold">{getValue('home.moat.3.title', '一件代發出海')}</p>
                    <p className="text-sm text-white/70">{getValue('home.moat.3.desc', 'YYC 倉就是你的海外倉——品牌零門檻出海')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 直播預告位（CMS 可編——每場直播後台改） */}
      <section className="py-14 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white border border-stone-200 rounded-3xl overflow-hidden text-center">
            <div className="relative h-40 md:h-52">
              <img
                src={getValue('home.live.image', '/assets/stock/live-stream-setup.jpg')}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-black/10" />
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 bg-white/95 text-red-600 text-xs font-bold px-3 py-1 rounded-full">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                {getValue('home.live.badge', '直播預告')}
              </div>
            </div>
            <div className="p-8 pt-5">
              <h2 className="text-2xl font-bold mb-2">{getValue('home.live.title', '下一場直播 · 即將公佈')}</h2>
              <p className="text-sm text-stone-500 mb-6 max-w-lg mx-auto">{getValue('home.live.subtitle', '坤哥直播試貨，香港品牌好貨即場試、即場優惠。開播前 WhatsApp 收通知。')}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/live?src=fblive" className="inline-flex items-center justify-center gap-2 bg-x1493-primary text-white font-bold px-6 py-3 rounded-2xl hover:brightness-110 transition">
                  <Radio size={18} />
                  {getValue('home.live.cta', '睇直播落地頁')}
                </Link>
                <Link to="/offers" className="inline-flex items-center justify-center gap-2 border-2 border-x1493-primary text-x1493-primary font-bold px-6 py-3 rounded-2xl hover:bg-x1493-primary hover:text-white transition">
                  {getValue('home.live.cta2', '上一場限時優惠')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* B2B 案例鈎子（首案例：滾水6腳） */}
      <section className="py-14">
        <div className="max-w-5xl mx-auto px-4">
          <div className="rounded-3xl overflow-hidden border border-stone-200 grid md:grid-cols-2">
            <div className="p-8 md:p-10">
              <p className="text-xs font-bold text-x1493-primary tracking-widest mb-2">{getValue('home.case.kicker', '首個入駐品牌')}</p>
              <h2 className="text-2xl font-bold mb-3">{getValue('home.case.title', '滾水6腳：由馬鞍山小店到出海旗艦')}</h2>
              <p className="text-sm text-stone-600 leading-relaxed mb-6">{getValue('home.case.body', '2010 年馬鞍山街坊小店，如今成為平台首個入駐品牌——直播試貨打信任、YYC 專線出貨全球。你的品牌，可以是下一個。')}</p>
              <Link to="/b2b" className="inline-flex items-center gap-2 bg-x1493-primary text-white font-bold px-6 py-3 rounded-2xl hover:brightness-110 transition">
                {getValue('home.case.cta', '免費預約入駐診斷')}
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="relative min-h-[260px] bg-x1493-dark">
              <img
                src={getValue('home.case.image', '/assets/stock/case-packing.jpg')}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, rgba(11,53,39,.55) 0%, rgba(11,53,39,.85) 100%)' }} />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                <img src="/logo-white.png" alt="滾水6腳" className="h-20 w-auto mb-4 drop-shadow" />
                <p className="text-white/85 text-xs leading-relaxed">2010 街坊小店 → 2026 出海旗艦<br />直播試貨 × YYC 包稅直郵</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gift Code CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-r from-x1493-primary to-x1493-secondary rounded-3xl p-8 md:p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">{getValue('home.giftCta.title', '想獲得專屬禮品碼？')}</h2>
            <p
              className="text-white/90 mb-8 max-w-xl mx-auto"
              dangerouslySetInnerHTML={getHtml('home.giftCta.description', '不定期有專屬優惠。聯繫我們，成為會員一族。')}
            />
            {whatsappReady ? (
              <a
                href={whatsappGiftLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-x1493-primary font-bold px-8 py-4 rounded-2xl hover:bg-stone-100 transition"
              >
                <MessageCircle size={22} />
                {getValue('home.giftCta.buttonText', '聯繫我們領取優惠')}
              </a>
            ) : (
              <Link
                to="/shipping"
                className="inline-flex items-center gap-2 bg-white text-x1493-primary font-bold px-8 py-4 rounded-2xl hover:bg-stone-100 transition"
              >
                <MessageCircle size={22} />
                {getValue('home.giftCta.buttonText', '聯繫我們')}（WhatsApp 開通前請看運送說明）
              </Link>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
